/**
 * Classroom Management – one workspace for a school's teachers and classes.
 *
 * Replaces two pages: Class Management (this route) and Teacher Approvals
 * (/school/approvals, which now redirects to the Teachers tab). The tab lives
 * in the URL so dashboard cards can link straight to it.
 *
 * Teachers also use /admin/classes: they get their own My Classes workspace
 * (screens/teacher/TeacherClassroom.tsx).
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { AnalyticsEmptyState, AnalyticsErrorState, AnalyticsPanel } from '../../Components/analytics/AnalyticsPanel';
import { TeachersTab } from '../../Components/classroom/TeachersTab';
import { ClassesTab } from '../../Components/classroom/ClassesTab';
import { StudentsTab } from '../../Components/classroom/StudentsTab';
import { OverviewTab } from '../../Components/classroom/OverviewTab';
import { useSchoolRoster } from '../../hooks/useSchoolRoster';
import { useSchoolResults } from '../../hooks/useSchoolResults';
import { getAllSchools } from '../../services/schoolManagementService';
import { listPartnerSchools } from '../../services/partnerService';
import TeacherClassroom from '../teacher/TeacherClassroom';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'classes', label: 'Classes' },
  { key: 'students', label: 'Students' },
  { key: 'teachers', label: 'Teachers' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground';

const ClassroomManagement = () => {
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = TABS.find((t) => t.key === params.get('tab'))?.key ?? 'overview';

  const role = profile?.role;
  const ownSchoolId =
    role === 'principal' ? profile?.managed_school_id || profile?.school_id : profile?.school_id || profile?.managed_school_id;
  const canPickSchool = role === 'superadmin' || role === 'admin' || role === 'partner';

  const [schools, setSchools] = useState<Array<{ id: string; name: string }>>([]);
  const [pickedSchoolId, setPickedSchoolId] = useState<string | null>(null);
  const schoolId = canPickSchool ? pickedSchoolId ?? ownSchoolId ?? null : ownSchoolId ?? null;

  useEffect(() => {
    if (!canPickSchool || !profile) return;
    let cancelled = false;
    (async () => {
      try {
        const list =
          role === 'partner'
            ? ((await listPartnerSchools()).schools ?? []).map((s) => ({ id: s.id, name: String(s.name ?? s.id) }))
            : (await getAllSchools(profile)).map((s) => ({ id: s.id, name: s.name }));
        if (!cancelled) setSchools(list.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('[ClassroomManagement] schools could not be listed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPickSchool, profile, role]);

  // A school account linked only through managed_school_id cannot read its
  // roster: the rules key a school admin off school_id. Fill it in once, as
  // the old Class Management page did.
  useEffect(() => {
    if (role !== 'school' || !profile?.uid || profile.school_id || !profile.managed_school_id) return;
    updateDoc(doc(db, 'users', profile.uid), {
      school_id: profile.managed_school_id,
      updatedAt: new Date().toISOString(),
    }).catch((err) => console.warn('[ClassroomManagement] could not set school_id:', err));
  }, [role, profile?.uid, profile?.school_id, profile?.managed_school_id]);

  const roster = useSchoolRoster(role === 'teacher' ? null : schoolId);
  const results = useSchoolResults(role === 'teacher' ? null : schoolId);
  const pending = roster.teachers.filter((t) => (t.approvalStatus ?? 'pending') === 'pending').length;

  if (role === 'teacher') return <TeacherClassroom />;

  /**
   * The tab, the open drawer and a list filter all live in the URL, so the
   * overview's "needs attention" links land exactly where they point and a
   * link to a drawer can be shared. Drawers replace the history entry rather
   * than add one, so Back leaves the page instead of reopening a drawer.
   */
  const go = (next: { tab?: TabKey; open?: string | null; filter?: string | null }, replace = false) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (next.tab) n.set('tab', next.tab);
        for (const key of ['open', 'filter'] as const) {
          if (next[key] === undefined) {
            // Switching tab clears the previous tab's drawer and filter.
            if (next.tab) n.delete(key);
          } else if (next[key]) n.set(key, next[key]!);
          else n.delete(key);
        }
        return n;
      },
      { replace }
    );
  const setTab = (next: TabKey) => go({ tab: next }, true);
  const openId = params.get('open');
  const setOpenId = (id: string | null) => go({ open: id }, true);
  const filter = params.get('filter');
  const setFilter = (f: string | null) => go({ filter: f }, true);

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">School administration</p>
            <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
              Classroom Management
            </h1>
          </div>
          {canPickSchool && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              School
              <select
                className={`${SELECT_CLASS} w-56`}
                value={schoolId ?? ''}
                onChange={(e) => setPickedSchoolId(e.target.value || null)}
              >
                <option value="">Select a school…</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </header>

        {!schoolId ? (
          <AnalyticsPanel title={canPickSchool ? 'Choose a school' : 'No school linked'}>
            <AnalyticsEmptyState
              title={canPickSchool ? 'Pick a school to manage' : 'Your account is not linked to a school'}
              description={
                canPickSchool
                  ? 'Select a school above to see its teachers and classes.'
                  : 'Ask your administrator to link your account to your school.'
              }
            />
          </AnalyticsPanel>
        ) : (
          <>
            <nav role="tablist" aria-label="Classroom Management sections" className="flex gap-1 border-b border-border">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition ${
                    tab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t.label}
                  {t.key === 'teachers' && pending > 0 && (
                    <span className="rounded-full bg-amber-500/15 px-1.5 text-[11px] font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                      {pending}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            {roster.error && <AnalyticsErrorState message={roster.error} />}

            {tab === 'overview' ? (
              <OverviewTab roster={roster} results={results} onGo={(next) => go(next)} />
            ) : tab === 'teachers' ? (
              <TeachersTab roster={roster} schoolId={schoolId} openId={openId} onOpen={setOpenId} status={filter} onStatus={setFilter} />
            ) : tab === 'students' ? (
              <StudentsTab roster={roster} schoolId={schoolId} view={filter} onView={setFilter} openId={openId} onOpen={setOpenId} />
            ) : (
              <ClassesTab
                key={schoolId}
                roster={roster}
                schoolId={schoolId}
                profile={profile}
                results={results}
                openId={openId}
                onOpen={setOpenId}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClassroomManagement;
